<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.save'), type: 'confirm', callback: handleConfirm },
    ]"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full p-2 flex flex-col"
  >
    <div v-if="purchaseOrder" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 mb-1 dark:text-gray-200">
          {{ $t('order.addProducts') }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300">
          {{ $t('components.purchaseOrder.actions.add.info') }}
        </p>
      </div>

      <div class="bg-white border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10 dark:bg-gray-900">
        <o-scroll class="py-4 px-8 h-full">
          <div class="mb-4 relative">
            <text-field v-model="query" :placeholder="$t('order.filterProducts')" leading="search" class="shadow-sm" />
            <div class="absolute right-0 transform mr-2 top-1/2 -translate-y-1/2">
              <o-button
                v-if="query.length"
                size="xs"
                variant="danger"
                transparent
                flat
                icon="x"
                @click="query = ''"
              />
            </div>
          </div>

          <template v-for="productCategory in filteredProducts" :key="productCategory.category ? productCategory.category.idProductCategory : 0">
            <div v-if="productCategory.products.length" class="pb-4">
              <h4 v-if="productCategory.category" class="text-sm text-gray-700 font-medium mb-2 dark:text-gray-200">
                {{ productCategory.category.name }}
              </h4>

              <div class="rounded bg-gray-50 flex flex-col divide-y border dark:bg-gray-900">
                <div v-for="product in productCategory.products" :key="product.product.idProduct" class="px-3 py-2 text-sm flex justify-between">
                  <div class="flex items-center space-x-2">
                    <o-avatar :items="[ product.product ]" />
                    <div>
                      <p class="text-gray-800 dark:text-gray-100 font-medium">
                        {{ product.product.name }}
                        <span v-if="product.product.inventory <= 0" class="text-red-600">
                          {{ $t('components.purchaseOrder.actions.add.outOfStock', { inventory: product.product.inventory }) }}
                        </span>
                        <template v-if="product.product.inventory > 0">
                          {{ $t('components.purchaseOrder.actions.add.inStock', { inventory: product.product.inventory }) }}
                        </template>

                        <o-chip v-if="product.product.inventory != null && product.product.inventoryReorder != null && product.product.inventory < product.product.inventoryReorder" size="sm" variant="warning">
                          {{ $t('pages.manage.index.products.lowStock') }}
                        </o-chip>
                      </p>
                      <p class="text-gray-600 dark:text-gray-300">
                        {{ product.product.wholesalePriceLabel }}
                      </p>
                    </div>
                  </div>

                  <div class="flex items-center space-x-2">
                    <div class="w-16">
                      <number-field v-model="product.quantity" slim />
                    </div>

                    <o-button size="sm" flat @click="handleAutoReorder(product)">
                      {{ $t('components.purchaseOrder.actions.add.auto', { inventory: product.product.inventoryMinimum || Math.abs(product.product.inventory) }) }}
                    </o-button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import Fuse from 'fuse.js'

import { OAvatar } from '@/components/elements'
import { TextField, NumberField } from '@/components/fields'

import { useProductStore } from '~/stores/useProductStore'

import type { PurchaseOrder } from '~/_api/models/purchaseOrder'

interface Props {
  purchaseOrder: PurchaseOrder
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()
const productStore = useProductStore()

const modal = ref<any>()
const query = ref('')
const products = ref<any[] | null>(null)

const filteredProducts = computed(() => {
  const productList = products.value
  const searchQuery = query.value

  if (searchQuery && productList) {
    const arrays = Object.values(productList).map(({ products }: any) => products)
    const items = ([] as any[]).concat(...arrays)
    const fuse = new Fuse(items, {
      keys: [
        'product.name',
      ],
    })
    const results = fuse.search(searchQuery)
    const options = [
      { products: results.map(({ item }) => item) },
    ]
    return options
  }

  return productList
})

const open = async () => {
  await productStore.sync()
  const list = productStore.all
  const idSupplier = (props.purchaseOrder as any).supplier.idSupplier

  const productsList = list
    .map(({ category, products: prods }: any) => {
      return {
        category,
        products: prods
          .filter((product: any) => product.supplier && product.supplier.idSupplier === idSupplier)
          .map((product: any) => {
            return {
              product,
              quantity: 0,
            }
          }),
      }
    })

  products.value = productsList
  modal.value.open()
}

const handleAutoReorder = (product: any) => {
  product.quantity = product.product.inventoryMinimum || Math.abs(product.product.inventory)
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const productList = products.value

    if (!productList) return

    const arrays = Object.values(productList).map(({ products: prods }: any) => prods)
    const list = ([] as any[]).concat(...arrays)

    const quantities = list
      .filter(({ quantity }) => !!quantity)
    const response = await $api.purchaseOrder.addItems(props.purchaseOrder, quantities)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('purchaseOrder.saved'),
        text: $i18n.t('components.purchaseOrder.actions.add.successInfo'),
        variant: 'success',
      })

      emit('success')

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })

    emit('error')
  }
}

defineExpose({ open })
</script>
